import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getRestaurants } from '../restaurantsApi';
import { COLOR, FONT, glass } from '../theme';

const RED = COLOR.accent;
const INK = COLOR.text;
const INK_MUTED = COLOR.textMuted;
const INK_FAINT = COLOR.textFaint;
const LINE = COLOR.glassBorder;

// Marketplace: guest lands here from "Order Room Dining" and picks a restaurant.
// Darcy's is the only one today (served by restaurantsApi's local fallback until
// the /restaurants endpoint is deployed). Tapping a card opens that restaurant's menu.
export default function RestaurantList() {
  const { hotelId, roomNumber } = useParams();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState(null); // null = loading

  useEffect(() => {
    let active = true;
    getRestaurants().then((list) => { if (active) setRestaurants(list); });
    return () => { active = false; };
  }, []);

  const openMenu = (r) => navigate(`/food/${hotelId}/${roomNumber}/${r.restaurantId}`);

  return (
    <div style={S.page}>
      <div style={S.topBar}>
        <button style={S.back} onClick={() => navigate(`/${hotelId}/${roomNumber}`)}>← Back</button>
        <div style={S.roomPill}><span style={S.pillDot} /><span style={S.pillText}>Room {roomNumber}</span></div>
      </div>

      <div style={S.head}>
        <p style={S.kicker}>ROOM DINING</p>
        <h1 style={S.title}>Choose a restaurant</h1>
        <p style={S.sub}>Order in from Halls Gap, delivered straight to your room.</p>
      </div>

      {restaurants === null ? (
        <div style={S.state}>
          <motion.div style={S.spinner} animate={{ rotate: 360 }} transition={{ repeat: Infinity, ease: 'linear', duration: 0.8 }} />
        </div>
      ) : restaurants.length === 0 ? (
        <div style={S.state}><p style={S.empty}>No restaurants available right now.</p></div>
      ) : (
        <div style={S.list}>
          {restaurants.map((r, i) => (
            <motion.button
              key={r.restaurantId}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              whileTap={{ scale: 0.985 }}
              style={S.card} onClick={() => openMenu(r)}
            >
              {r.heroImage && (
                <div style={S.thumb}>
                  <img src={r.heroImage} alt={r.name} style={S.thumbImg} />
                </div>
              )}
              <div style={S.info}>
                <div style={S.name}>{r.name}</div>
                {r.cuisine ? <div style={S.cuisine}>{r.cuisine}</div> : null}
                <div style={S.meta}>
                  <span style={S.openDot} /> Open
                  <span style={S.sep}>·</span>
                  {r.etaMins} min
                </div>
              </div>
              <span style={S.arrow}>›</span>
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
}

const S = {
  page: { minHeight: '100vh', background: COLOR.bg, maxWidth: '430px', margin: '0 auto', color: INK,
    fontFamily: FONT, paddingBottom: '40px' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 16px 8px' },
  back: { background: COLOR.chipBg, border: `1px solid ${LINE}`, borderRadius: '20px', padding: '8px 14px', color: INK, fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  roomPill: { display: 'flex', alignItems: 'center', gap: '7px', background: COLOR.chipBg, border: `1px solid ${LINE}`, borderRadius: '40px', padding: '7px 14px' },
  pillDot: { width: '6px', height: '6px', borderRadius: '50%', background: '#16A34A', boxShadow: '0 0 7px rgba(22,163,74,0.5)', display: 'inline-block' },
  pillText: { fontSize: '12px', fontWeight: '600', color: COLOR.chipText, letterSpacing: '0.3px' },

  head: { padding: '18px 20px 14px' },
  kicker: { fontSize: '10px', letterSpacing: '3px', color: RED, fontWeight: '700', margin: '0 0 6px' },
  title: { fontSize: '26px', fontWeight: '800', color: INK, margin: '0 0 6px', letterSpacing: '-0.6px' },
  sub: { fontSize: '13.5px', color: INK_MUTED, margin: 0, lineHeight: 1.45 },

  list: { display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 14px 0' },
  card: { display: 'flex', alignItems: 'center', gap: '14px', width: '100%', textAlign: 'left', cursor: 'pointer',
    ...glass({ borderRadius: '18px', padding: '12px' }), fontFamily: 'inherit' },
  thumb: { width: '68px', height: '68px', borderRadius: '14px', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
  monogram: { fontSize: '30px', fontWeight: '800', letterSpacing: '-1px' },
  info: { flex: 1, minWidth: 0 },
  name: { fontSize: '17px', fontWeight: '800', color: INK, letterSpacing: '-0.3px', marginBottom: '2px' },
  cuisine: { fontSize: '12.5px', color: INK_MUTED, marginBottom: '6px' },
  meta: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: INK_FAINT, fontWeight: '600' },
  openDot: { width: '6px', height: '6px', borderRadius: '50%', background: '#16A34A', display: 'inline-block' },
  sep: { color: 'rgba(245,245,247,0.25)' },
  arrow: { fontSize: '24px', color: INK_FAINT, flexShrink: 0, fontWeight: '400' },

  state: { display: 'flex', justifyContent: 'center', padding: '60px 0' },
  spinner: { width: '30px', height: '30px', borderRadius: '50%', border: '3px solid rgba(255,255,255,0.12)', borderTopColor: RED },
  empty: { fontSize: '14px', color: INK_MUTED },
};
